<template>
  <div ref="modalEl" class="modal fade" tabindex="-1" aria-labelledby="apmTitle" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h2 id="apmTitle" class="modal-title fs-5">Création des bons de paiement livreurs</h2>
            <small class="text-muted">Deux modes pour créer un bon par livreur.</small>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
        </div>

        <div class="modal-body">
          <div class="apm-grid">
            <!-- ── 01 Mode manuel ───────────────────────────────── -->
            <section class="apm-mode" aria-labelledby="apm-manual">
              <header class="apm-mode-head">
                <span class="apm-num">01</span>
                <div>
                  <h3 id="apm-manual">Mode manuel</h3>
                  <p>Déclencher la création à la demande</p>
                </div>
              </header>

              <!-- A. Ajout individuel -->
              <div class="apm-block">
                <div class="apm-block-head">
                  <span class="apm-letter">A</span>
                  <div>
                    <h4>Ajout individuel</h4>
                    <p>Créer le bon du livreur sélectionné.</p>
                  </div>
                </div>
                <div class="apm-block-body">
                  <label class="apm-label" for="apm-ind-agency">Agence</label>
                  <v-select
                    v-model="indAgency"
                    input-id="apm-ind-agency"
                    class="custom-select-height mb-10"
                    :options="agencies"
                    label="name"
                    placeholder="Choisir une agence"
                  />
                  <label class="apm-label" for="apm-ind-driver">Livreur</label>
                  <v-select
                    v-model="indDriver"
                    input-id="apm-ind-driver"
                    class="custom-select-height"
                    :options="indDrivers"
                    :get-option-label="driverLabel"
                    :loading="indLoading"
                    :disabled="!indAgency"
                    placeholder="Sélectionner un livreur"
                  >
                    <template #no-options>Aucun livreur avec des colis à payer dans cette agence.</template>
                  </v-select>
                  <small class="text-muted d-block mt-5">Seuls les livreurs ayant des colis à payer sont proposés.</small>
                  <button type="button" class="btn apm-btn-blue w-100 mt-10" :disabled="!indDriver || !!busy" @click="createIndividual">
                    <span v-if="busy === 'ind'" class="spinner-border spinner-border-sm me-5" role="status"></span>
                    <i v-else class="ph ph-plus me-5" aria-hidden="true"></i>Ajouter
                  </button>
                </div>
              </div>

              <!-- B. Ajout groupé -->
              <div class="apm-block">
                <div class="apm-block-head">
                  <span class="apm-letter">B</span>
                  <div>
                    <h4>Ajout groupé</h4>
                    <p>Créer un bon pour chaque livreur de cette agence.</p>
                  </div>
                </div>
                <div class="apm-block-body">
                  <label class="apm-label" for="apm-grp-agency">Agence</label>
                  <v-select
                    v-model="grpAgency"
                    input-id="apm-grp-agency"
                    class="custom-select-height"
                    :options="agencies"
                    label="name"
                    placeholder="Choisir une agence"
                  />
                  <small v-if="grpAgency" class="text-muted d-block mt-5">
                    <template v-if="grpLoading">Calcul…</template>
                    <template v-else-if="grpEligible.length">
                      {{ grpEligible.length }} livreur(s) · {{ grpParcels }} colis à payer
                    </template>
                    <template v-else>Aucun livreur avec des colis à payer dans cette agence.</template>
                  </small>
                  <button
                    type="button"
                    class="btn btn-primary w-100 mt-10"
                    :disabled="!grpAgency || !grpEligible.length || !!busy"
                    @click="createGrouped"
                  >
                    <span v-if="busy === 'grp'" class="spinner-border spinner-border-sm me-5" role="status"></span>
                    <i v-else class="ph ph-play me-5" aria-hidden="true"></i>Auto Add
                  </button>
                </div>
              </div>
            </section>

            <!-- ── 02 Mode automatique ──────────────────────────── -->
            <section class="apm-mode" aria-labelledby="apm-auto">
              <header class="apm-mode-head">
                <span class="apm-num">02</span>
                <div>
                  <h3 id="apm-auto">Mode automatique</h3>
                  <p>Planifier la création sans intervention</p>
                </div>
              </header>

              <div v-if="scheduleLoading" class="text-center py-4"><span class="spinner-border spinner-border-sm"></span></div>
              <form v-else class="apm-auto" novalidate @submit.prevent="saveSchedule">
                <label class="apm-label" for="apm-auto-agency">Agences</label>
                <v-select
                  v-model="schedule.agency"
                  input-id="apm-auto-agency"
                  class="custom-select-height"
                  :options="agencyOptionsAll"
                  label="name"
                  :clearable="false"
                />
                <small class="text-muted d-block mt-5">Une agence ou toutes les agences</small>

                <label class="apm-label mt-15" id="apm-days">Jours de création</label>
                <div class="apm-days" role="group" aria-labelledby="apm-days">
                  <button
                    v-for="d in weekDays"
                    :key="d.value"
                    type="button"
                    :class="{ 'is-on': schedule.days.includes(d.value) }"
                    :aria-pressed="schedule.days.includes(d.value)"
                    :title="d.full"
                    @click="toggleDay(d.value)"
                  >
                    {{ d.short }}
                  </button>
                </div>
                <span v-if="scheduleErrors.days" class="apm-err">{{ scheduleErrors.days }}</span>

                <label class="apm-label mt-15" for="apm-time">Heure de création</label>
                <div class="apm-time">
                  <i class="ph ph-clock" aria-hidden="true"></i>
                  <input id="apm-time" v-model="schedule.run_time" type="time" class="form-control" required />
                </div>
                <span v-if="scheduleErrors.run_time" class="apm-err">{{ scheduleErrors.run_time }}</span>
                <small v-else class="text-muted d-block mt-5">Heure du Maroc</small>

                <div class="form-check form-switch apm-switch mt-15">
                  <input id="apm-active" v-model="schedule.active" class="form-check-input" type="checkbox" role="switch" />
                  <label class="form-check-label" for="apm-active">
                    {{ schedule.active ? "Planification activée" : "Planification désactivée" }}
                  </label>
                </div>

                <button type="submit" class="btn apm-btn-blue w-100 mt-15" :disabled="savingSchedule">
                  <span v-if="savingSchedule" class="spinner-border spinner-border-sm me-5" role="status"></span>
                  <i v-else class="ph ph-floppy-disk me-5" aria-hidden="true"></i>Enregistrer la planification
                </button>

                <div class="apm-banner mt-15">
                  <i class="ph ph-calendar-dots" aria-hidden="true"></i>
                  <div>
                    <strong>Création aux jours et à l'heure définis.</strong>
                    <small v-if="saved.active && saved.next_run">Prochaine création : {{ dateTime(saved.next_run) }}</small>
                    <small v-else>Planification désactivée : aucune création automatique.</small>
                    <small v-if="saved.last_run_at">
                      Dernière exécution : {{ dateTime(saved.last_run_at) }} — {{ saved.last_run_created ?? 0 }} bon(s) créé(s)
                    </small>
                    <small v-if="saved.last_run_error" class="text-danger">Erreur : {{ saved.last_run_error }}</small>
                  </div>
                </div>
              </form>
            </section>
          </div>

          <!-- ── Rules ────────────────────────────────────────── -->
          <div class="apm-rules">
            <strong>Règles communes</strong>
            <span><i class="ph-fill ph-check-circle" aria-hidden="true"></i>Un bon par livreur</span>
            <span><i class="ph-fill ph-check-circle" aria-hidden="true"></i>Aucun colis en double</span>
            <span><i class="ph-fill ph-check-circle" aria-hidden="true"></i>Aucun bon vide</span>
          </div>
          <p class="apm-foot">Création uniquement • Enregistrer et Payer restent manuels</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, reactive, ref, watch } from "vue";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import api from "@/utils/axios";
import { useAppToast } from "@/composables/useAppToast";

interface Agency {
  id: number | null;
  name: string;
}
interface EligibleDriver {
  agency_id: number;
  livreur_id: number;
  livreur_name: string;
  nb_colis: number;
}
interface SavedSchedule {
  agency_id: number | null;
  days: number[];
  run_time: string;
  active: boolean;
  next_run: string | null;
  last_run_at: string | null;
  last_run_created: number | null;
  last_run_error: string | null;
}

const weekDays = [
  { value: 1, short: "Lun", full: "Lundi" },
  { value: 2, short: "Mar", full: "Mardi" },
  { value: 3, short: "Mer", full: "Mercredi" },
  { value: 4, short: "Jeu", full: "Jeudi" },
  { value: 5, short: "Ven", full: "Vendredi" },
  { value: 6, short: "Sam", full: "Samedi" },
  { value: 7, short: "Dim", full: "Dimanche" },
];
const ALL_AGENCIES: Agency = { id: null, name: "Toutes les agences" };

export default defineComponent({
  name: "AutoAddPayNotesModal",
  emits: ["created"],
  setup(_, { emit, expose }) {
    const { showToast } = useAppToast();
    const modalEl = ref<HTMLElement | null>(null);
    const busy = ref<"" | "ind" | "grp">("");

    const agencies = ref<Agency[]>([]);
    const agencyOptionsAll = computed(() => [ALL_AGENCIES, ...agencies.value]);

    const driverLabel = (d: EligibleDriver) => `${d.livreur_name} — ${d.nb_colis} colis`;
    const dateTime = (v: string) => {
      const d = new Date(v.replace(" ", "T"));
      return isNaN(d.getTime())
        ? v
        : d.toLocaleString("fr-FR", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    };

    async function fetchEligible(agencyId: number) {
      const res = await api.get("/api/auth/eligible_pay_notes", { params: { agency_id: agencyId } });
      return (res.data || []) as EligibleDriver[];
    }

    // ── A. Individual ──
    const indAgency = ref<Agency | null>(null);
    const indDriver = ref<EligibleDriver | null>(null);
    const indDrivers = ref<EligibleDriver[]>([]);
    const indLoading = ref(false);
    async function loadIndividual() {
      indDriver.value = null;
      indDrivers.value = [];
      if (!indAgency.value?.id) return;
      indLoading.value = true;
      try {
        indDrivers.value = await fetchEligible(indAgency.value.id);
      } catch {
        showToast("Erreur lors du chargement des livreurs", "error");
      } finally {
        indLoading.value = false;
      }
    }
    watch(indAgency, loadIndividual);

    // ── B. Grouped ──
    const grpAgency = ref<Agency | null>(null);
    const grpEligible = ref<EligibleDriver[]>([]);
    const grpLoading = ref(false);
    const grpParcels = computed(() => grpEligible.value.reduce((s, d) => s + Number(d.nb_colis || 0), 0));
    async function loadGrouped() {
      grpEligible.value = [];
      if (!grpAgency.value?.id) return;
      grpLoading.value = true;
      try {
        grpEligible.value = await fetchEligible(grpAgency.value.id);
      } catch {
        showToast("Erreur lors du chargement des livreurs", "error");
      } finally {
        grpLoading.value = false;
      }
    }
    watch(grpAgency, loadGrouped);

    async function generate(kind: "ind" | "grp", payload: Record<string, unknown>, question: string) {
      const confirm = await Swal.fire({
        title: "Confirmer la création",
        text: question,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Oui, créer",
        cancelButtonText: "Annuler",
        reverseButtons: true,
      });
      if (!confirm.isConfirmed) return;

      busy.value = kind;
      try {
        const res = await api.post("/api/auth/auto_add_pay_notes", payload);
        if (res.data?.success) {
          const n = Number(res.data.created || 0);
          showToast(n ? `${n} bon(s) de paiement créé(s)` : "Aucun bon créé : plus de colis à payer", n ? "success" : "warning");
          if (n) emit("created", res.data.refs || []);
          // refresh: drivers already paid disappear from the lists
          if (kind === "ind") loadIndividual();
          else loadGrouped();
        } else {
          showToast(res.data?.message ?? "Erreur lors de la création", "error");
        }
      } catch (error: any) {
        showToast(error?.response?.data?.message || "Erreur lors de la création des bons", "error");
      } finally {
        busy.value = "";
      }
    }

    const createIndividual = () =>
      indAgency.value &&
      indDriver.value &&
      generate(
        "ind",
        { agency_id: indAgency.value.id, livreur_id: indDriver.value.livreur_id },
        `Créer le bon de ${indDriver.value.livreur_name} (${indDriver.value.nb_colis} colis) ?`
      );
    const createGrouped = () =>
      grpAgency.value &&
      generate(
        "grp",
        { agency_id: grpAgency.value.id },
        `Créer un bon pour chacun des ${grpEligible.value.length} livreur(s) de ${grpAgency.value.name} ?`
      );

    // ── 02. Planning ──
    const scheduleLoading = ref(false);
    const savingSchedule = ref(false);
    const schedule = reactive<{ agency: Agency; days: number[]; run_time: string; active: boolean }>({
      agency: ALL_AGENCIES,
      days: [],
      run_time: "06:00",
      active: false,
    });
    const saved = ref<SavedSchedule>({
      agency_id: null,
      days: [],
      run_time: "06:00",
      active: false,
      next_run: null,
      last_run_at: null,
      last_run_created: null,
      last_run_error: null,
    });
    const scheduleErrors = reactive<Record<string, string>>({});

    function applySchedule(data: SavedSchedule) {
      saved.value = data;
      schedule.agency = agencies.value.find((a) => a.id === data.agency_id) || ALL_AGENCIES;
      schedule.days = [...(data.days || [])];
      schedule.run_time = data.run_time || "06:00";
      schedule.active = !!data.active;
    }

    function toggleDay(d: number) {
      delete scheduleErrors.days;
      const i = schedule.days.indexOf(d);
      if (i === -1) schedule.days.push(d);
      else schedule.days.splice(i, 1);
      schedule.days.sort((a, b) => a - b);
    }

    async function saveSchedule() {
      Object.keys(scheduleErrors).forEach((k) => delete scheduleErrors[k]);
      if (schedule.active && !schedule.days.length) scheduleErrors.days = "Choisissez au moins un jour de création.";
      if (!/^\d{2}:\d{2}$/.test(schedule.run_time || "")) scheduleErrors.run_time = "L'heure de création est obligatoire.";
      if (Object.keys(scheduleErrors).length || savingSchedule.value) return;

      savingSchedule.value = true;
      try {
        const res = await api.post("/api/auth/pay_notes_schedule", {
          agency_id: schedule.agency?.id ?? null,
          days: schedule.days,
          run_time: schedule.run_time,
          active: schedule.active,
        });
        if (res.data?.success) {
          applySchedule(res.data.data);
          showToast(schedule.active ? "Planification enregistrée et activée." : "Planification enregistrée (désactivée).", "success");
        } else {
          showToast(res.data?.message ?? "Erreur lors de l'enregistrement", "error");
        }
      } catch (error: any) {
        const errs = error?.response?.data?.errors || {};
        Object.keys(errs).forEach((k) => (scheduleErrors[k.split(".")[0]] = errs[k][0]));
        showToast(error?.response?.data?.message || "Erreur lors de l'enregistrement", "error");
      } finally {
        savingSchedule.value = false;
      }
    }

    // ── Open ──
    async function open() {
      indAgency.value = null;
      grpAgency.value = null;
      Object.keys(scheduleErrors).forEach((k) => delete scheduleErrors[k]);
      if (modalEl.value) Modal.getOrCreateInstance(modalEl.value).show();

      scheduleLoading.value = true;
      try {
        const [ag, sc] = await Promise.all([api.get("/api/auth/pay_notes_agencies"), api.get("/api/auth/pay_notes_schedule")]);
        agencies.value = ag.data || [];
        applySchedule(sc.data.data);
      } catch {
        showToast("Erreur lors du chargement", "error");
      } finally {
        scheduleLoading.value = false;
      }
    }
    expose({ open });

    onBeforeUnmount(() => {
      if (modalEl.value) Modal.getInstance(modalEl.value)?.dispose();
    });

    return {
      modalEl,
      busy,
      agencies,
      agencyOptionsAll,
      driverLabel,
      dateTime,
      indAgency,
      indDriver,
      indDrivers,
      indLoading,
      grpAgency,
      grpEligible,
      grpLoading,
      grpParcels,
      createIndividual,
      createGrouped,
      weekDays,
      schedule,
      saved,
      scheduleErrors,
      scheduleLoading,
      savingSchedule,
      toggleDay,
      saveSchedule,
      open,
    };
  },
});
</script>

<style scoped>
.apm-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}
.apm-mode {
  overflow: hidden;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
}
.apm-mode-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  background: var(--ui-info-soft);
}
.apm-mode-head h3 {
  margin: 0;
  color: var(--ui-info);
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
  text-transform: uppercase;
}
.apm-mode-head p,
.apm-block-head p {
  margin: 2px 0 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.apm-num {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--ui-info);
  color: #fff;
  font-weight: var(--ui-fw-black);
}
.apm-block {
  margin: 14px;
  overflow: hidden;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
}
.apm-block-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: var(--ui-surface-sunken);
}
.apm-block-head h4 {
  margin: 0;
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
}
.apm-letter {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--ui-accent);
  color: #fff;
  font-weight: var(--ui-fw-black);
}
.apm-block-body {
  padding: 14px;
}
.apm-auto {
  padding: 16px 18px 18px;
}
.apm-label {
  display: block;
  margin-bottom: 6px;
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-heavy);
}
.apm-btn-blue {
  border-color: var(--ui-info);
  background: var(--ui-info);
  color: #fff;
  font-weight: var(--ui-fw-bold);
}
.apm-btn-blue:hover:not(:disabled) {
  filter: brightness(0.92);
  color: #fff;
}
.apm-days {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
}
.apm-days button {
  padding: 9px 0;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-sunken);
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
  transition: all var(--ui-dur-fast) var(--ui-ease);
}
.apm-days button.is-on {
  border-color: var(--ui-info);
  background: var(--ui-info);
  color: #fff;
}
.apm-time {
  position: relative;
}
.apm-time i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--ui-text-muted);
  font-size: 18px;
  pointer-events: none;
}
.apm-time .form-control {
  padding-left: 38px;
}
.apm-switch .form-check-label {
  font-weight: var(--ui-fw-semibold);
}
.apm-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-radius: var(--ui-radius-md);
  background: var(--ui-info-soft);
  color: var(--ui-info);
}
.apm-banner i {
  font-size: 26px;
}
.apm-banner small {
  display: block;
  color: var(--ui-text-secondary);
}
.apm-err {
  display: block;
  margin-top: 6px;
  color: var(--ui-danger);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-semibold);
}
.apm-rules {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 28px;
  margin-top: 18px;
  padding: 14px 18px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
}
.apm-rules strong {
  color: var(--ui-info);
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
}
.apm-rules span {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: var(--ui-fw-semibold);
}
.apm-rules i {
  color: var(--ui-accent);
  font-size: 22px;
}
.apm-foot {
  margin: 10px 0 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  text-align: center;
}
@media (max-width: 991.98px) {
  .apm-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
@media (max-width: 575.98px) {
  .apm-days {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
